import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  IconButton,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useApiClient } from "../useApiClient";
import { useState, useEffect } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import { API_CONFIG, getApiUrl, NAVIGATION_CONFIG } from "../config/api";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import { Phone } from "../types/phone";
import { CustomerAddress } from "../types/address";
import { AddressesSection } from "../components/AddressesSection";
import { AddressLookupModal } from "../components/AddressLookupModal";
import { AddressModal } from "../components/AddressModal";
import { SharedAddressSaveWarningDialog } from "../components/dialogs/SharedAddressSaveWarningDialog";

interface Resident {
  externalId: string;
  title: string;
  firstName: string;
  lastName: string;
}

interface CareHomeDetail {
  externalId: string;
  name: string;
  contactPerson: string | null;
  contactEmail: string | null;
  phones: Phone[];
  addresses: CustomerAddress[];
  locationType?: number;
  residents?: Resident[];
}

interface ValidationErrors {
  [key: string]: string | undefined;
}

const CARE_LOCATION_TYPE_OPTIONS = [
  { value: 0, label: "Unknown" },
  { value: 1, label: "Sheltered Housing" },
  { value: 2, label: "Nursing Home" },
  { value: 3, label: "Residential Shared Address" },
  { value: 4, label: "Day Centre" },
  { value: 5, label: "Head Office" },
  { value: 6, label: "Home Visit" },
  { value: 7, label: "EMI Home" },
  { value: 8, label: "Young EMI Home" },
  { value: 14, label: "Glasses Direct" },
  { value: 15, label: "Hospital" },
  { value: 16, label: "Respite Centre" },
  { value: 17, label: "Supported Living" },
];

export default function CareHomeDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { fromPage, pageSize } = location.state || {
    fromPage: 0,
    pageSize: 10,
  };
  const { callApi } = useApiClient();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<CareHomeDetail>>({
    name: "",
    contactPerson: "",
    contactEmail: "",
    phones: [],
    addresses: [],
    locationType: 0, // default to 0 (Unknown)
  });
  const [phoneValidationErrors, setPhoneValidationErrors] = useState<
    (string | undefined)[]
  >([]);

  // Address modals
  const [isAddressLookupModalOpen, setIsAddressLookupModalOpen] =
    useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<
    CustomerAddress | undefined
  >(undefined);

  // Toast state
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">("success");

  // Convert to address dialog
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [saveWarningOpen, setSaveWarningOpen] = useState(false);

  // Toast helper method
  const showToast = (message: string, severity: "success" | "error" = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const isNew = id === "new";

  const fetchCareHomeDetails = async () => {
    if (isNew) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await callApi(
        getApiUrl(API_CONFIG.ENDPOINTS.CAREHOMES, id)
      );
      setFormData(result.data);
    } catch (err: any) {
      console.error("Failed to fetch care home details:", err);
      setError(err.message || "Failed to load shared address details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCareHomeDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleBack = () => {
    navigate(NAVIGATION_CONFIG.ROUTES.CARE_HOMES, {
      state: {
        returnedFromDetails: true,
        page: fromPage,
        pageSize: pageSize,
      },
    });
  };
  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: string; type?: string } }
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "locationType" ? Number(value) : value,
    }));

    // Clear validation error for the field being edited
    if (validationErrors[name as string]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name as string]: undefined,
      }));
    }

    // Clear general error if there is one
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async () => {
    // Validate phone numbers before submit
    const errors = (formData.phones || []).map((phone) => {
      if (!phone.number) return undefined;
      const parsed = parsePhoneNumberFromString(phone.number, "GB");
      return parsed && parsed.isValid() ? undefined : "Invalid phone number";
    });
    setPhoneValidationErrors(errors);
    if (errors.some((e) => !!e)) {
      setError("Please correct invalid phone numbers.");
      showToast("Please check errors before saving", "error");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setValidationErrors({});

      if (isNew) {
        const response = await callApi(getApiUrl(API_CONFIG.ENDPOINTS.CAREHOMES), {
          method: "POST",
          data: formData,
        });
        const newExternalId = response?.data?.externalId;
        if (newExternalId) {
          showToast("Shared address created successfully");
          // Navigate to the newly created care home's detail page
          navigate(NAVIGATION_CONFIG.getCareHomeDetailRoute(newExternalId), {
            replace: true,
          });
          return;
        }
      } else {
        const response = await callApi(getApiUrl(API_CONFIG.ENDPOINTS.CAREHOMES, id), {
          method: "PUT",
          data: formData,
        });
        // Update the form data with the response to ensure we have the latest data
        setFormData(response.data);
        showToast("Shared address updated successfully");
      }
    } catch (err: any) {
      if (err.response?.status === 400 && err.response?.data?.errors) {
        // Transform array of errors into key-value pairs
        const errorMap: ValidationErrors = {};
        err.response.data.errors.forEach(
          (error: { field: string; error: string }) => {
            // Convert field name to match form field names (camelCase)
            const fieldName =
              error.field.charAt(0).toLowerCase() + error.field.slice(1);
            errorMap[fieldName] = error.error;
          }
        );
        setValidationErrors(errorMap);
        setError("Please correct the validation errors below.");
        showToast("Please check errors before saving", "error");
      } else {
        console.error("Failed to save care home:", err);
        setError(err.message || "Failed to save shared address");
        showToast("Failed to save shared address", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id || isNew) return;
    try {
      setLoading(true);
      setError(null);
      setValidationErrors({});

      await callApi(getApiUrl(API_CONFIG.ENDPOINTS.CAREHOMES, id), {
        method: "DELETE",
      });
      navigate(NAVIGATION_CONFIG.ROUTES.CARE_HOMES);
    } catch (err: any) {
      if (err.response?.status === 400 && err.response?.data?.errors) {
        // Transform array of errors into key-value pairs
        const errorMap: ValidationErrors = {};
        err.response.data.errors.forEach(
          (error: { field: string; error: string }) => {
            // Convert field name to match form field names (camelCase)
            const fieldName =
              error.field.charAt(0).toLowerCase() + error.field.slice(1);
            errorMap[fieldName] = error.error;
          }
        );
        setValidationErrors(errorMap);
        setError("Cannot delete shared address due to validation errors.");
      } else {
        console.error("Failed to delete care home:", err);
        setError(err.message || "Failed to delete shared address");
      }
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleOpenConvertDialog = () => {
    setConvertDialogOpen(true);
  };

  const handleCloseConvertDialog = () => {
    setConvertDialogOpen(false);
  };

  const handleConvertToAddress = async () => {
    if (!id || isNew) return;

    try {
      setLoading(true);
      setError(null);

      const response = await callApi(
        `${getApiUrl(API_CONFIG.ENDPOINTS.CAREHOMES)}/convert/${id}`,
        { method: "POST" }
      );

      const customerExternalId = response?.data?.customerExternalId;
      if (customerExternalId) {
        showToast("Shared address successfully converted to customer address");
        // Navigate to the customer details page
        navigate(NAVIGATION_CONFIG.getCustomerDetailRoute(customerExternalId));
      } else {
        throw new Error("No customer ID returned from conversion");
      }
    } catch (err: any) {
      console.error("Failed to convert shared space:", err);
      const errorMessage = err.message || "Failed to convert shared address to address";
      setError(errorMessage);
      showToast(errorMessage, "error");
    } finally {
      setLoading(false);
      setConvertDialogOpen(false);
    }
  };

  // Address handlers
  const handleAddAddress = () => {
    setIsAddressLookupModalOpen(true);
  };

  const handleAddressSelect = (address: CustomerAddress) => {
    setFormData((prev) => ({
      ...prev,
      addresses: [...(prev.addresses || []), address],
    }));
    setIsAddressLookupModalOpen(false);
  };

  const handleRemoveAddress = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      addresses: (prev.addresses || []).filter((_, i) => i !== index),
    }));
  };

  const handleEditAddress = (address: CustomerAddress) => {
    setSelectedAddress(address);
    setIsAddressModalOpen(true);
  };

  const handleAddNewAddress = () => {
    setIsAddressLookupModalOpen(false);
    setIsAddressModalOpen(true);
  };

  const handleSaveAddress = (address: CustomerAddress) => {
    setFormData((prev) => {
      const index = (prev.addresses || []).findIndex(
        (a) => a.externalId === address.externalId
      );
      if (index === -1) {
        return { ...prev, addresses: [...(prev.addresses || []), address] };
      }
      const updatedAddresses = [...(prev.addresses || [])];
      updatedAddresses[index] = address;
      return { ...prev, addresses: updatedAddresses };
    });
    setSelectedAddress(undefined);
    setIsAddressModalOpen(false);
  };

  const handleUpdateAddress = (
    index: number,
    updates: Partial<CustomerAddress>
  ) => {
    setFormData((prev) => {
      const newAddresses = [...(prev.addresses || [])];
      if (updates.isPrimary) {
        newAddresses.forEach((addr) => {
          addr.isPrimary = false;
        });
      }
      newAddresses[index] = { ...newAddresses[index], ...updates };
      return { ...prev, addresses: newAddresses };
    });
  };

  const handleCustomerClick = (customerId: string) => {
    const customerUrl = `${window.location.origin}${NAVIGATION_CONFIG.ROUTES.CUSTOMERS}/${customerId}`;
    window.open(customerUrl, '_blank');
  };

  const handleAddCustomerToSharedSpace = () => {
    if (!isNew && id) {
      navigate(`${NAVIGATION_CONFIG.getCustomerDetailRoute('new')}?sharedspace=${id}`);
    }
  };

  if (loading && !isNew) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="30%" height={40} sx={{ mb: 2 }} />{" "}
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Skeleton variant="text" height={56} />
            </Grid>
            {[...Array(2)].map((_, index) => (
              <Grid size={6} key={index}>
                <Skeleton variant="text" height={56} />
              </Grid>
            ))}
            {[...Array(3)].map((_, index) => (
              <Grid size={4} key={`row2-${index}`}>
                <Skeleton variant="text" height={56} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ pl: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
      >
        <Typography variant="h4" component="h1" color="primary">
          {isNew ? "New Shared Address" : "Shared Address Details"}
        </Typography>
        <IconButton
          onClick={handleBack}
          sx={{
            bgcolor: "background.paper",
            boxShadow: 1,
            "&:hover": { bgcolor: "background.paper", opacity: 0.9 },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box display="flex" gap={3}>
        <Box flex={1} minWidth={0}>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth
              required
              label="Shared Address Name"
              name="name"
              value={formData.name || ""}
              onChange={handleInputChange}
              error={Boolean(validationErrors.name)}
              helperText={validationErrors.name}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Contact Person"
              name="contactPerson"
              value={formData.contactPerson || ""}
              onChange={handleInputChange}
              error={Boolean(validationErrors.contactPerson)}
              helperText={validationErrors.contactPerson}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              label="Contact Email"
              name="contactEmail"
              type="email"
              value={formData.contactEmail || ""}
              onChange={handleInputChange}
              error={Boolean(validationErrors.contactEmail)}
              helperText={validationErrors.contactEmail}
            />
          </Grid>
          <Grid size={6}>
            <FormControl fullWidth>
              <InputLabel id="care-location-type-label">
                Location Type
              </InputLabel>
              <Select
                labelId="care-location-type-label"
                id="care-location-type"
                name="locationType"
                value={formData.locationType ?? 0}
                label="Location Type"
                onChange={(e) =>
                  handleInputChange({
                    target: {
                      name: "locationType",
                      value: e.target.value.toString(), // pass as string
                    },
                  })
                }
                error={Boolean(validationErrors.locationType)}
              >
                {CARE_LOCATION_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Phone Numbers
            </Typography>
            {validationErrors.phones && (
              <Typography
                color="error"
                variant="caption"
                sx={{ display: "block", mb: 1 }}
              >
                {validationErrors.phones}
              </Typography>
            )}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              {formData.phones && formData.phones.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No phone numbers added.
                </Typography>
              )}
              {formData.phones &&
                formData.phones.map((phone, idx) => {
                  const error = phoneValidationErrors[idx];
                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <TextField
                        label="Phone Number"
                        value={phone.number}
                        onChange={(e) => {
                          const newPhones = [...(formData.phones || [])];
                          const input = e.target.value;
                          let valid = false;
                          let parsed = null;
                          let detectedType = "Unknown";
                          if (input) {
                            debugger;
                            if (input.startsWith("+")) {
                              parsed = parsePhoneNumberFromString(input); // no region
                            } else {
                              parsed = parsePhoneNumberFromString(input, "GB"); // fallback region
                            }
                            valid = parsed ? parsed.isValid() : false;
                            if (parsed && valid) {
                              detectedType = parsed.getType() || "Unknown";
                            }
                          }
                          newPhones[idx] = {
                            ...phone,
                            number: input,
                            type: detectedType,
                          };
                          setFormData((prev) => ({
                            ...prev,
                            phones: newPhones,
                          }));
                          // Validate phone number
                          const errors = [...phoneValidationErrors];
                          errors[idx] = !input
                            ? undefined
                            : valid
                            ? undefined
                            : "Invalid phone number";
                          setPhoneValidationErrors(errors);
                        }}
                        sx={{ flex: 1, minWidth: 180 }}
                        error={!!error}
                        helperText={error}
                      />
                      <TextField
                        label="Type"
                        value={phone.type}
                        InputProps={{ readOnly: true }}
                        sx={{ width: 120, minWidth: 100, flexShrink: 0 }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={phone.isPrimary}
                            onChange={(e) => {
                              const newPhones = (formData.phones || []).map(
                                (p, i) => ({
                                  ...p,
                                  isPrimary:
                                    i === idx ? e.target.checked : false,
                                })
                              );
                              setFormData((prev) => ({
                                ...prev,
                                phones: newPhones,
                              }));
                            }}
                            color="primary"
                          />
                        }
                        label="Primary"
                      />
                      <IconButton
                        aria-label="delete"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            phones: (prev.phones || []).filter(
                              (_, i) => i !== idx
                            ),
                          }));
                          setPhoneValidationErrors((prev) =>
                            prev.filter((_, i) => i !== idx)
                          );
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  );
                })}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    phones: [
                      ...(prev.phones || []),
                      {
                        number: "",
                        isPrimary: (prev.phones?.length ?? 0) === 0,
                        type: "MOBILE",
                      },
                    ],
                  }));
                  setPhoneValidationErrors((prev) => [...prev, undefined]);
                }}
                sx={{ mt: 1 }}
              >
                Add Phone
              </Button>
            </Paper>
          </Grid>
        </Grid>
        <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            {!isNew && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleOpenDeleteDialog}
                disabled={true}/*loading*/
                startIcon={<DeleteIcon />}
              >
                Delete Shared Address
              </Button>
            )}
            {!isNew && id && formData.residents && formData.residents.length === 1 && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleOpenConvertDialog}
                disabled={loading}
                startIcon={<PersonIcon />}
              >
                Convert to Address
              </Button>
            )}
          </Box>
          <Button
            variant="contained"
            onClick={isNew ? handleSubmit : () => setSaveWarningOpen(true)}
            disabled={loading || !formData.name}
          >
            {isNew ? "Create Shared Address" : "Save Changes"}
          </Button>
        </Box>
      </Paper>
    </Box>

    <Box flex={1} minWidth={0}>
      <AddressesSection
        addresses={formData.addresses || []}
        validationErrors={validationErrors}
        hideGroupButton={true}
        onAddAddress={handleAddAddress}
        onEditAddress={handleEditAddress}
        onRemoveAddress={handleRemoveAddress}
        onUpdateAddress={handleUpdateAddress}
      />
    </Box>
  </Box>
      {/* Residents DataGrid */}
      {Array.isArray(formData.residents) && formData.residents.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Residents
            </Typography>
            {!isNew && (
              <IconButton
                onClick={handleAddCustomerToSharedSpace}
                title="Add customer to this shared address"
                sx={{ 
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    opacity: 0.1
                  }
                }}
              >
                <GroupIcon />
              </IconButton>
            )}
          </Box>
          <Paper variant="outlined" sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={formData.residents.map((r) => ({ id: r.externalId, ...r }))}
              columns={[
                {
                  field: "actions",
                  headerName: "",
                  width: 60,
                  sortable: false,
                  filterable: false,
                  disableColumnMenu: true,
                  renderCell: (params) => (
                    <IconButton
                      size="small"
                      onClick={() => handleCustomerClick(params.row.externalId)}
                      title="Open customer details"
                      sx={{ color: 'primary.main' }}
                    >
                      <PersonIcon />
                    </IconButton>
                  ),
                },
                {
                  field: "externalId",
                  headerName: "External ID",
                  flex: 1,
                  minWidth: 80,
                },
                {
                  field: "title",
                  headerName: "Title",
                  flex: 0.5,
                  minWidth: 80,
                },
                {
                  field: "firstName",
                  headerName: "First Name",
                  flex: 1,
                  minWidth: 120,
                },
                {
                  field: "lastName",
                  headerName: "Last Name",
                  flex: 1,
                  minWidth: 120,
                },
              ]}
              getRowId={(row) => row.externalId}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    externalId: false,
                  },
                },
              }}
              autoHeight={false}
            />
          </Paper>
        </Box>
      )}

      {/* Address Modals */}
      <AddressLookupModal
        isVisible={isAddressLookupModalOpen}
        onClose={() => setIsAddressLookupModalOpen(false)}
        onAddressSelect={handleAddressSelect}
        onAddAddress={handleAddNewAddress}
      />

      <AddressModal
        isVisible={isAddressModalOpen}
        onClose={() => {
          setSelectedAddress(undefined);
          setIsAddressModalOpen(false);
        }}
        address={selectedAddress}
        onSave={handleSaveAddress}
      />

      {/* Convert to Address Dialog */}
      <Dialog
        open={convertDialogOpen}
        onClose={handleCloseConvertDialog}
        aria-labelledby="convert-dialog-title"
        aria-describedby="convert-dialog-description"
        maxWidth="md"
      >
        <DialogTitle id="convert-dialog-title">Convert Shared Address to Address</DialogTitle>
        <DialogContent>
          <DialogContentText id="convert-dialog-description">
            Please ensure that all changes are saved before continuing. This will take the primary address 
            and add it as the primary address for{" "}
            <strong>
              {formData.residents && formData.residents.length === 1 
                ? `${formData.residents[0].firstName} ${formData.residents[0].lastName}`
                : "the resident"}
            </strong>.
            <br /><br />
            All other shared address information (phone numbers, emails, additional addresses, and contact person) will be lost. 
            The shared address will be deleted.
            <br /><br />
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConvertDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConvertToAddress} color="warning" autoFocus>
            Convert to Address
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Shared Address</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this shared address? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Warning Dialog */}
      <SharedAddressSaveWarningDialog
        open={saveWarningOpen}
        onClose={() => setSaveWarningOpen(false)}
        onContinue={() => {
          setSaveWarningOpen(false);
          handleSubmit();
        }}
      />

      {/* Toast Notification */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setToastOpen(false)} 
          severity={toastSeverity}
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
